import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';
import { useCleanupRRACBodyHelpers } from '../hooks/useCleanupRRACBodyHelpers';

// ============================================================================
// Component
// ============================================================================

/**
 * Thin theme wrapper around the `apartment-sync` block. Lets the section
 * appear as a normal registered component in `pages.json` while delegating
 * rendering to the block. The wrapped block is swappable via the editor
 * (constrained to the Integrations category).
 */
export function ApartmentSyncSection() {
  useCleanupRRACBodyHelpers();
  const blockSlot = useBlockSlot('apartmentsyncsection.block', {
    defaultValue: 'apartment-sync',
  });

  return (
    <section className="apartment-sync-section">
      <EditableToken path="apartmentsyncsection.block" as="div">
        {blockSlot ? (
          <SectionContextProvider sectionId={blockSlot.sectionId}>
            <blockSlot.BlockComponent sectionId={blockSlot.sectionId} />
          </SectionContextProvider>
        ) : (
          <div className="apartment-sync-empty">No block selected</div>
        )}
      </EditableToken>

      <style>{`
        .apartment-sync-section {
          width: 100%;
          padding-top: clamp(2rem, 4vw, 4rem);
          background: #ffffff;
        }
        .apartment-sync-empty {
          font-family: var(--vibe-font-body);
          color: var(--vibe-color-text-muted);
          text-align: center;
          padding: 2rem 1rem;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'ApartmentSyncSection',
  displayName: 'Apartment Sync Section',
  tokens: {
    'apartmentsyncsection.block': {
      label: 'Block',
      type: 'block',
      defaultValue: 'apartment-sync',
      acceptedBlockCategories: ['Integrations'],
    },
  },
};

(ApartmentSyncSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ApartmentSyncSection;
